import useAxiosAbortStore from '@/stores/base/axios-abort';

import axios from 'axios';
import { useAuthorityStore } from '@/stores/authority';
import queryString from 'query-string';
import {
  handleHttpError,
  handleRequestData,
  handleRequestHeaders,
  handleResponseHeaders,
  logRequest,
  logRequestAndResponse,
  object2FormData,
} from './utils/request-util';
import { HttpStatusCode } from './utils/http-status-code';

const useAuthority = useAuthorityStore();

// 创建实例
const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 2000000,
});

/**
 * 请求拦截器
 */
axiosInstance.interceptors.request.use(
  async (config) => {
    // 添加取消请求控制器，便于在适当时机取消请求
    const curMenu =
      useAuthority.pageFunctionObj[useAuthority.menuUrl]?.menuCode;
    const abortController = new AbortController();
    config.signal = abortController.signal;
    useAxiosAbortStore().addController(abortController, curMenu);
    handleRequestHeaders(config.headers, config).catch((err) => {
      // 打印（失败）请求日志
      logRequest(config);

      throw err;
    });
    // 添加自定义内容
    config.custom = {
      requestTime: new Date(),
    };

    axiosInstance.interceptors.request.customConfig = config.customConfig;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);
/**
 *  响应拦截器
 *
 * */
axiosInstance.interceptors.response.use(
  (response) => {
    // 输出响应日志
    logRequestAndResponse(response);
    // 处理响应头
    handleResponseHeaders(response.headers);

    return handleRequestData(response.data);
  },
  async (error) => {
    if (error?.code === 'ERR_CANCELED') {
      return new Promise(() => {});
    }
    // 反馈的错误码是602的，就当正常操作处理，由接口自行处理
    if (error.response?.status === HttpStatusCode.BusinessExceptionNeedDeal) {
      // 输出响应日志
      logRequestAndResponse(error.response);
      // 处理响应头
      handleResponseHeaders(error.response.headers);
      return handleRequestData(error.response.data);
    }

    if (error.response) {
      logRequestAndResponse(error.response);
    }

    if (axios.isCancel(error)) {
      // 对axios进行封装，设置 request 和 response 的处理操作
      return new Promise(() => {});
    }

    // 处理Http错误

    if (error.response) {
      // 静默请求（如心跳）：跳过统一错误提示，直接交由外部 catch
      if (error.config?.silent) {
        return Promise.reject(error);
      }
      const responseResult = await handleHttpError(
        error.response.status,
        error.response.data,
        error.response,
        axiosInstance,
      );
      if (responseResult === true) {
        // 已处理异常，则中断Promise链接
        return new Promise(() => {});
      }
      if (responseResult === false) {
        // 未处理异常，则交由外部处理
        return Promise.reject(error);
      }
      return Promise.resolve(responseResult);
    }
    // 未处理异常，则交由外部处理
    return Promise.reject(error);
  },
);

/**
 * Get
 * @param {string} url
 * @param {Object} [params]
 * @param {Object} [config]
 * @returns {Promise<AxiosResponse<any>>}
 */
axiosInstance.get = (url, params, config) => {
  const axiosConfig = { ...config, method: 'get' };
  if (params) {
    axiosConfig.originData = params;
    axiosConfig.params = params;
    axiosConfig.paramsSerializer = (paramsForSerializer) => {
      return queryString.stringify(paramsForSerializer);
    };
  }

  return axiosInstance.request(url, axiosConfig);
};

/**
 * Json格式的Post（application/json）
 * @param {string} url
 * @param {Object} [data]
 * @param {Object} [config]
 * @returns {Promise<AxiosResponse<any>>}
 */
axiosInstance.postJson = (url, data, config) => {
  const axiosConfig = {
    ...config,
    method: 'post',
    headers: {
      ...config.headers,
      'Content-Type': 'application/json',
    },
  };
  if (data) {
    axiosConfig.originData = data;
    axiosConfig.data = data;
  }

  return axiosInstance.request(url, axiosConfig);
};

/**
 * 预设参数的通用Post（application/x-www-form-urlencoded）
 * @param {string} url
 * @param {Object} [data]
 * @param {Object} [config]
 * @returns {Promise<AxiosResponse<any>>}
 */
axiosInstance.postPreset = (url, data, config) => {
  const axiosConfig = { ...config, method: 'post' };
  if (data) {
    axiosConfig.originData = data;
    axiosConfig.data = queryString.stringify(data);
  }

  return axiosInstance.request(url, axiosConfig);
};

/**
 * 带文件的Post（multipart/form-data）
 * @param {string} url
 * @param {FormData} [data]
 * @param {Object} [config]
 * @returns {Promise<AxiosResponse<any>>}
 */
axiosInstance.postWithFile = (url, data, config) => {
  const axiosConfig = { ...config, method: 'post' };
  if (data) {
    axiosConfig.originData = data;
    axiosConfig.data = object2FormData(data);
  }

  return axiosInstance.request(url, axiosConfig);
};

export default axiosInstance;
